import { FC, useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';

import SearchListSelector from '~src/shared/selectors/searchListSelector/SearchListSelector';
import { OptionItem } from '~src/shared/selectors/DdSearchContent';
import {
  ObjectivesQueryI,
  MSFilterTypes,
  OkrLabel,
  ObjectiveStatus,
} from '~src/store/okrs/interfaces';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { StoreI } from '~src/rootReducer';
import { fetchLabels, FetchLabelsParams } from '~src/store/okrs/labels/actions';
import { PaginationI } from '~src/interfaces/pagination.interface';

type LabelsFilterI = Pick<ObjectivesQueryI, MSFilterTypes.Label>;

interface StateProps {
  labels: OkrLabel[];
  pagination: PaginationI | null;
  loadingLabels: boolean;
}

interface DispatchProps {
  fetchLabels(name?: string, queryObj?: FetchLabelsParams, loadMore?: boolean): ApiReduxResp<OkrLabel[]>;
}

interface OwnProps {
  initialOpts?: LabelsFilterI[MSFilterTypes.Label];
  applyFilter(filterName: MSFilterTypes, ids: LabelsFilterI[MSFilterTypes.Label]): void;
}

type Props = OwnProps & StateProps & DispatchProps;

const LabelsSelector: FC<Props> = (props) => {
  const [selectorInactive, setSelectorInactive] = useState<boolean>(
    !props.initialOpts || !props.initialOpts.length,
  );

  useEffect(() => {
    fetchLabelsW();
  }, []);

  const optionsList = useMemo(() => {
    if (!props.labels) return [];

    return props.labels.reduce<OptionItem[]>((acc, label) => {
      acc.push({
        id: label.id,
        name: label.name,
      });
      return acc;
    }, []);
  }, [props.labels]);

  const fetchLabelsW = (params?: FetchLabelsParams | string | null, loadMore?: boolean): Promise<any> => {
    let queryObj: FetchLabelsParams = { sort: 'name', per_page: 10 };

    if (typeof params === 'string' && !!params) {
      queryObj = { ...queryObj, search: params };
    } else if (!!params && typeof params === 'object') {
      queryObj = { ...queryObj, ...params };
    }

    if (loadMore && props.pagination) {
      queryObj.page = props.pagination.next_page;
    }

    return props.fetchLabels('', queryObj, loadMore);
  };

  const applyChecked = (ids: ObjectiveStatus[]) => {
    props.applyFilter(MSFilterTypes.Label, ids);
    setSelectorInactive(!ids.length);
  };

  const resetSelected = () => {
    props.applyFilter(MSFilterTypes.Label, undefined);
    setSelectorInactive(true);
  };

  const getInitiallySelectedOptionName = async (id: string) => {
    const { response } = await props.fetchLabels(id);
    return response ? `${response.name}` : '';
  };

  return (
    <SearchListSelector
      multiSelect
      initialOpt={props.initialOpts}
      inactive={selectorInactive}
      inactiveKey="x.okr.objectives.labels_selector.inactive_label"
      onSelect={applyChecked}
      optionsList={optionsList}
      loadingSearch={props.loadingLabels}
      fetchItems={fetchLabelsW}
      noMoreItems={!!(props.pagination && !props.pagination.next_page)}
      nothingFoundKey="x.okr.objectives.labels_selector.no_labels_found"
      resetSelected={resetSelected}
      getInitiallySelectedOptionName={getInitiallySelectedOptionName}
    />
  );
};

const mapState2Props = ({ okrs }: StoreI): StateProps => ({
  labels: okrs.labels.list,
  pagination: okrs.labels.pagination,
  loadingLabels: okrs.labels.loadingList,
});

const mapDispatch2Props = (dispatch: any): DispatchProps => ({
  fetchLabels: (name, queryObj, loadMore) => dispatch(fetchLabels(name, queryObj, loadMore)),
});

export default connect(mapState2Props, mapDispatch2Props)(LabelsSelector);
